import type { Iso3166TwoDigitCountryCode } from '@onefootprint/request-types';
import type React from 'react';
import { forwardRef, useId, useRef, useState } from 'react';
import mergeRefs from 'react-merge-refs';
import { useFormattedPhoneValues } from '../../../../hooks/use-formatted-values';
import { useInputMask } from '../../../../hooks/use-input-mask';
import Label from '../../../label';
import type { PhoneInputProps } from '../../phone-input.types';
import { getNationalNumber } from '../../phone-input.utils';
import CountryPicker from './components/country-picker';
import { defaultPreference, preferences } from './input.constants';

type InputProps = PhoneInputProps & {
  countryCode: Iso3166TwoDigitCountryCode;
  prefix: string;
  selectTrigger?: {
    isOpen?: boolean;
    onClick?: () => void;
  };
};

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      countryCode,
      defaultValue,
      disabled = false,
      hasError = false,
      id: baseId,
      label,
      onBlur,
      onChange,
      onChangeText,
      onFocus,
      prefix,
      selectTrigger,
      size,
      value,
      ...props
    }: InputProps,
    ref,
  ) => {
    const [isFocused, setFocus] = useState(false);
    const localRef = useRef<HTMLInputElement>(null);
    const internalId = useId();
    const id = baseId || internalId;
    const countryPreferences = preferences[countryCode] || defaultPreference;

    const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
      setFocus(false);
      onBlur?.(event);
    };

    const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
      setFocus(true);
      onBlur?.(event);
    };

    const { inputRef: maskInputRef, handleChange: handleMaskChange } = useInputMask(countryPreferences.mask);
    const { formattedValue, formattedDefaultValue } = useFormattedPhoneValues({
      value,
      defaultValue,
      mask: countryPreferences.mask,
      prefix,
      getNationalNumber,
    });

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      // Apply mask formatting
      handleMaskChange(event);

      const formattedValue = event.currentTarget.value;
      const inputValueWithCountryCode = `${prefix} ${formattedValue}`;
      const modifiedEvent = {
        ...event,
        target: {
          ...event.target,
          value: inputValueWithCountryCode,
        },
      };
      onChange?.(modifiedEvent);
      onChangeText?.(modifiedEvent.target.value);
    };

    return (
      <div>
        {label ? <Label htmlFor={id}>{label}</Label> : null}
        {/* biome-ignore lint/a11y/useKeyWithClickEvents: markup unchanged from the styled component this replaces */}
        <div
          className="fp-input fp-phone-input fp-phone-input-container fp-custom-appearance"
          data-size="default"
          data-has-error={hasError}
          data-has-focus={isFocused}
          data-disabled={disabled}
          onClick={() => {
            localRef.current?.focus();
          }}
        >
          <CountryPicker code={countryCode} disabled={disabled} onClick={selectTrigger?.onClick} prefix={prefix} />
          <input
            {...props}
            autoComplete="tel-national"
            data-has-error={hasError}
            data-has-focus={isFocused}
            defaultValue={formattedDefaultValue || undefined}
            disabled={disabled}
            ref={mergeRefs([localRef, maskInputRef, ref])}
            id={id}
            key={countryCode}
            onBlur={handleBlur}
            onChange={handleChange}
            onFocus={handleFocus}
            placeholder={countryPreferences.placeholder}
            type="tel"
            value={formattedValue || undefined}
            /** Do not change/remove these classes */
            className="fp-input fp-phone-input fp-phone-input-field fp-custom-appearance"
          />
        </div>
      </div>
    );
  },
);

export default Input;
